import React, { useState, useEffect, Suspense, useMemo } from 'react';
import Header from './components/Header';
import Hero from './components/Hero';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import ContentProtection from './components/ContentProtection';
import { PAGES, SeoPage } from './seo/pages';
import { useSeo } from './hooks/useSeo';

// Lazy load components
const Services = React.lazy(() => import('./components/Services'));
const StudioSection = React.lazy(() => import('./components/StudioSection'));
const CataboostSection = React.lazy(() => import('./components/CataboostSection'));
const Process = React.lazy(() => import('./components/Process'));
const AboutUs = React.lazy(() => import('./components/AboutUs'));
const CaseStudies = React.lazy(() => import('./components/CaseStudies'));
const Testimonials = React.lazy(() => import('./components/Testimonials'));
const FAQ = React.lazy(() => import('./components/FAQ'));
const Contact = React.lazy(() => import('./components/Contact'));
const ThankYou = React.lazy(() => import('./components/ThankYou'));
const ContentPage = React.lazy(() => import('./components/ContentPage'));
const NotFound = React.lazy(() => import('./components/NotFound'));
const CookieConsent = React.lazy(() => import('./components/CookieConsent'));

const normalizePath = (p: string): string => {
  if (!p || p === '/') return '/';
  return p.endsWith('/') ? p : `${p}/`;
};

const App: React.FC = () => {
  const [currentPath, setCurrentPath] = useState<string>(() => normalizePath(window.location.pathname));

  // Handle backward compatibility for hashes (#/thank-you, #/multumesc, or anchor scrolling)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash === '#/thank-you' || hash === '#/multumesc') {
      window.history.replaceState({}, '', '/thank-you/');
      setCurrentPath('/thank-you/');
      return;
    }

    if (hash && hash.startsWith('#') && hash.length > 1) {
      const targetId = hash.substring(1);
      const el = document.getElementById(targetId);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth' });
        }, 150);
      }
    }
  }, []);

  // Listen to popstate (Browser Back/Forward buttons)
  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(normalizePath(window.location.pathname));
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Intercept clicks on internal links (href starting with /) to use pushState and scroll to top
  useEffect(() => {
    const handleGlobalClick = (e: MouseEvent) => {
      // Ignore clicks with modifier keys or secondary mouse buttons
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

      const target = e.target as HTMLElement | null;
      const anchor = target?.closest('a') as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      // Only handle internal links starting with '/' that are not protocol-relative ('//')
      if (href.startsWith('/') && !href.startsWith('//')) {
        // If link contains hash anchor on current page, smooth-scroll to it
        if (href.includes('#')) {
          const [pathPart, hashPart] = href.split('#');
          const targetNorm = normalizePath(pathPart || '/');
          const currentNorm = normalizePath(window.location.pathname);
          if (targetNorm === currentNorm) {
            e.preventDefault();
            const targetEl = document.getElementById(hashPart);
            if (targetEl) {
              targetEl.scrollIntoView({ behavior: 'smooth' });
            }
            return;
          }
        }

        e.preventDefault();
        window.history.pushState({}, '', href);
        const nextNorm = normalizePath(window.location.pathname);
        setCurrentPath(nextNorm);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    document.addEventListener('click', handleGlobalClick);
    return () => document.removeEventListener('click', handleGlobalClick);
  }, []);

  // Find page match in PAGES (including alias for /blog/how-to-add-meta-pixel-to-shopify/)
  const matchedPage: SeoPage | undefined = useMemo(() => {
    if (currentPath === '/blog/how-to-add-meta-pixel-to-shopify/') {
      return PAGES.find(p => p.path === '/2023/12/16/how-to-add-meta-facebook-pixel-to-shopify-using-customer-events/');
    }
    return PAGES.find(p => p.path === currentPath);
  }, [currentPath]);

  const isHome = currentPath === '/';
  const isThankYou = currentPath === '/thank-you/';
  const isNotFound = !isHome && !isThankYou && !matchedPage;

  // Determine active SEO metadata
  const seoConfig = useMemo(() => {
    if (isHome) {
      return {
        path: '/',
        title: '82 Marketing | High-Performance E-commerce Growth & PPC Agency',
        description: 'E-commerce PPC agency managing Google, Meta, Bing and TikTok Ads for online stores. Monthly contracts, transparent reporting, a free account analysis.',
        noindex: false
      };
    }
    if (isThankYou) {
      return {
        path: '/thank-you/',
        title: 'Thank You | 82 Marketing',
        description: 'Thank you for contacting 82 Marketing. We will review your store details and get back to you shortly.',
        noindex: true,
        robotsDirective: 'noindex,follow'
      };
    }
    if (matchedPage) {
      return {
        path: matchedPage.path,
        title: matchedPage.title,
        description: matchedPage.description,
        noindex: matchedPage.noindex
      };
    }
    return {
      path: currentPath,
      title: 'Page Not Found (404) | 82 Marketing',
      description: 'The requested page could not be found.',
      noindex: true,
      robotsDirective: 'noindex',
      isNotFound: true
    };
  }, [isHome, isThankYou, matchedPage, currentPath]);

  // Apply SEO hooks on every route change
  useSeo(seoConfig);

  return (
    <div className="bg-gray-50 text-gray-800 font-sans">
      <ContentProtection />

      {isHome && (
        <>
          <Header isHomePage={true} />
          <main>
            <Hero />
            <Suspense fallback={<div className="py-20 bg-gray-50" />}>
              <Services />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-gray-900" />}>
              <StudioSection />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-cyan-950" />}>
              <CataboostSection />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-white" />}>
              <Process />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-gray-50" />}>
              <AboutUs />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-white" />}>
              <CaseStudies />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-gray-50" />}>
              <Testimonials />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-white" />}>
              <FAQ />
            </Suspense>
            <Suspense fallback={<div className="py-20 bg-gray-50" />}>
              <Contact />
            </Suspense>
          </main>
          <Footer isHomePage={true} />
        </>
      )}

      {isThankYou && (
        <>
          <Header isHomePage={false} />
          <main>
            <Suspense fallback={<div className="h-screen flex items-center justify-center bg-gray-50" />}>
              <ThankYou />
            </Suspense>
          </main>
          <Footer isHomePage={false} />
        </>
      )}

      {matchedPage && (
        <Suspense fallback={<div className="min-h-screen bg-white flex items-center justify-center" />}>
          <ContentPage page={matchedPage} />
        </Suspense>
      )}

      {isNotFound && (
        <Suspense fallback={<div className="min-h-screen bg-gray-50 flex items-center justify-center" />}>
          <NotFound />
        </Suspense>
      )}

      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
      <ScrollToTop />
    </div>
  );
};

export default App;
